import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, Select, Switch } from '@mui/material';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { addNewTank, addTank, editTank } from '../../features/fueladmin/fuelSlice';
import { CircularProgress } from '@mui/material';
import { useridSelector } from '../../features/user/UserSlice';
import { tankKeys, tankContent } from '../../utils/fuelOptions';
import Checkbox from '@mui/material/Checkbox';

const defaultTuningParams = {
    volDiffTimeGap: 60,
    minVolDiff: 8,
    trendChangeBufferPeriod: 300,
    trendConfirmStreamCount: 2,
    streamUnitPeriod: 30,
    streamMaxTime: 3600,
    driveStartTimeout: 0,
    haltStartTimeout: 30,
    stopSpeedCutoff: 2,
    driveSpeedCutoff: 2,
    minFillDuration: 0,
    maxFillDuration: 599,
    minTheftDuration: 0,
    maxTheftDuration: 599,
    minFillFlowRatePerMinute: 2,
    maxFillFlowRatePerMinute: 500,
    minTheftFlowRatePerMinute: 2,
    maxTheftFlowRatePerMinute: 500,
    trendWhenDriving: 0,
}

const TankForm = ({ view, fuelSetup, assetId, tankIndex }) => {

    const dispatch = useDispatch();
    const userid = useSelector(useridSelector);
    const [loading, setLoading] = useState(false);
    const [tank, setTank] = useState({});
    const [driveTrend, setDriveTrend] = useState(false);

    useEffect(() => {
        //console.log("Tank form index", tankIndex);
        const { tankId, name, noOfSensors, content, volume, expectedMileage, expectedHourly, minFillThreshold, minTheftThreshold, sensorIndices, tuningParams } = fuelSetup.tanks[tankIndex - 1];
        let si = [];
        if (sensorIndices) {
            si = sensorIndices;
        } else {
            if (noOfSensors) {
                si = Array(parseInt(noOfSensors)).fill(0);
            }
        }
        let tp = { ...defaultTuningParams };
        if (tuningParams) {
            tp = { ...tp, ...tuningParams };
        }
        setTank({ assetId, setupId: fuelSetup.setupId, tankIndex, tankId, name, noOfSensors, content, volume, expectedMileage, expectedHourly, minFillThreshold, minTheftThreshold, sensorIndices: si, tuningParams: tp });
        //console.log("setting trend when driving", tp.trendWhenDriving);
        if (tp.trendWhenDriving === 1) {
            setDriveTrend(true);
        } else {
            setDriveTrend(false);
        }
    }, [view, fuelSetup, assetId, tankIndex]);


    const updateTank = (key, value) => {
        //console.log("UPD TAnk", key, value);
        value = isNaN(parseInt(value)) ? value : parseInt(value);
        setTank({ ...tank, [key]: value });
    };

    const updateContent = (value) => {
        setTank({ ...tank, content: value });
    };

    const updateTankSize = (value) => {
        const tankSize = value;
        const threshold = Math.ceil(value * 0.05);
        setTank({ ...tank, volume: tankSize, minFillThreshold: threshold, minTheftThreshold: threshold, tuningParams: { ...tank.tuningParams, minFillThreshold: threshold, minTheftThreshold: threshold } });
    };

    const updateTankTuning = (key, value) => {
        //console.log("UPD Tuning", key, value);
        if (key === 'minFillThreshold' || key === 'minTheftThreshold') {
            setTank({ ...tank, [key]: parseInt(value), tuningParams: { ...tank.tuningParams, [key]: parseInt(value) } });
        } else {
            setTank({ ...tank, tuningParams: { ...tank.tuningParams, [key]: parseInt(value) } });
        }
    }

    const updateTrendWhenDriving = (event) => {
        //console.log("IN uptun", event.target.checked);
        let value = null;
        if (event.target.checked === true) {
            value = 1;
            setDriveTrend(true);
        } else {
            value = 0;
            setDriveTrend(false);
        }

        //console.log("new tune", value);
        setTank({ ...tank, tuningParams: { ...tank.tuningParams, trendWhenDriving: value } });
    }
    const [formValid, setFormValid] = useState(false);


    useEffect(() => {
        let valid = true;
        tankKeys.forEach(k => {
            if (!tank.hasOwnProperty(k)) {
                valid = false;
            }
        })
        if (tank.noOfSensors > fuelSetup.sensors.length) {
            valid = false;
        }
        setFormValid(valid);
    }, [tank, fuelSetup])

    const saveTank = () => {
        setLoading(true);
        let finalTank = tank;
        if (!tank.name) {
            finalTank = { ...tank, name: tankIndex > 1 ? `${tank.content}-${tankIndex}` : tank.content }
        }
        finalTank = { ...finalTank, userid, tankIndex };
        //console.log("Sasving tank", view, finalTank.tuningParams.minTheftDuration, finalTank.tuningParams.maxTheftDuration);
        if (view === 'addTank') {
            dispatch(addNewTank(finalTank));
            dispatch(addTank(finalTank));
        } else if (view === 'editTank') {
            dispatch(addNewTank(finalTank));
            dispatch(editTank(finalTank));
        } else {
            setLoading(false);
        }

    };

    if (loading) {
        return (
            <div>
                <FormControl sx={{ m: 1, width: '100ch' }} >
                    <CircularProgress />
                </FormControl>
            </div>
        );
    } else {
        return (
            <div>
                <div>
                    <FormControl sx={{ m: 1, width: '90ch' }} >
                        <Typography variant="body1" display="inline">
                            <span><b>Tank: {tankIndex}</b></span>
                        </Typography>
                    </FormControl>
                    <FormControl sx={{ m: 1, width: '25ch' }} >
                        <InputLabel id="tankContent">Tank for?</InputLabel>
                        <Select
                            variant="standard"
                            id="tankContent"
                            value={tank.content ?? ""}
                            onChange={(e) => updateContent(e.target.value)}
                        >
                            {
                                tankContent.map(ev => <MenuItem key={ev} value={ev}>{ev}</MenuItem>)
                            }
                        </Select>
                    </FormControl>
                    {
                        tank.content &&
                        <Fragment>

                            <FormControl sx={{ m: 1, width: '25ch' }} >
                                <TextField
                                    label="Name of Tank?"
                                    id="name"
                                    //value={tank.name ?? tankIndex > 1 ? `${tank.content}-${tankIndex}` : tank.content}
                                    value={tank.name ? tank.content : 'Diesel'}
                                    onChange={(e) => updateTank(e.target.id, e.target.value)}
                                    sx={{ m: 1, width: '22ch' }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">{fuelSetup.noOfTanks > 1 ? `${tank.content}-${tankIndex}` : `${tank.content}`} </InputAdornment>,
                                    }}
                                    variant="standard"
                                />
                            </FormControl>
                        </Fragment>
                    }
                    <FormControl sx={{ m: 1, width: '25ch' }} >
                        <TextField
                            label="Tank Size in Liters?"
                            id="volume"
                            value={tank.volume ?? 0}
                            onChange={(e) => updateTankSize(e.target.value)}
                            sx={{ m: 1, width: '22ch' }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                            }}
                            variant="standard"
                            type="number"
                        />
                    </FormControl>
                    <FormControl sx={{ m: 1, width: '25ch' }} >
                        <TextField
                            label="How many Sensors in this Tank?"
                            id="noOfSensors"
                            value={tank.noOfSensors ?? 0}
                            onChange={(e) => updateTank(e.target.id, e.target.value)}
                            sx={{ m: 1, width: '22ch' }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">Sensor Count: </InputAdornment>,
                            }}
                            variant="standard"
                            type="number"
                        />
                    </FormControl>

                    <FormControl sx={{ m: 1, width: '25ch' }} >
                        <TextField
                            label="Normal Mileage of this Tank?"
                            id="expectedMileage"
                            value={tank.expectedMileage ?? 0}
                            onChange={(e) => updateTank(e.target.id, e.target.value)}
                            sx={{ m: 1, width: '22ch' }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">Km/Liter: </InputAdornment>,
                            }}
                            variant="standard"
                            type="number"
                        />
                    </FormControl>
                    <FormControl sx={{ m: 1, width: '25ch' }} >
                        <TextField
                            label="Liters/Hour Consumed on Idle?"
                            id="expectedHourly"
                            value={tank.expectedHourly ?? 0}
                            onChange={(e) => updateTank(e.target.id, e.target.value)}
                            sx={{ m: 1, width: '22ch' }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">Liters/Hour: </InputAdornment>,
                            }}
                            variant="standard"
                            type="number"
                        />
                    </FormControl>
                    {
                        tank.tuningParams &&
                        <Fragment>
                            <FormControl sx={{ m: 1, width: '90ch' }} >
                                <Typography variant="body1" display="inline">
                                    <span><b>Advanced Tuning Parameters</b></span>
                                </Typography>
                            </FormControl>
                            <FormControl sx={{ m: 1, width: '25ch' }} >
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={driveTrend}
                                        onChange={updateTrendWhenDriving}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                }
                                    label="Detect Fill/theft when Driving?"
                                />
                            </FormControl>
                            <FormControl sx={{ m: 1, width: '25ch' }} >
                                <TextField
                                    label="Minimum Filling to be Detected?"
                                    id="minFillThreshold"
                                    value={tank.minFillThreshold ?? 0}
                                        onChange={(e) => { updateTank(e.target.id, e.target.value); updateTankTuning(e.target.id, e.target.value) }}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Minimum Theft to be Detected?"
                                        id="minTheftThreshold"
                                        value={tank.minTheftThreshold ?? 0}
                                        onChange={(e) => { updateTank(e.target.id, e.target.value); updateTankTuning(e.target.id, e.target.value) }}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Vol Diff timegap?"
                                        id="volDiffTimeGap"
                                        value={tank.tuningParams.volDiffTimeGap ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Minimum Vol diff for Trend?"
                                        id="minVolDiff"
                                        value={tank.tuningParams.minVolDiff ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Trend Change Buffer period?"
                                        id="trendChangeBufferPeriod"
                                        value={tank.tuningParams.trendChangeBufferPeriod ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Stream count to confirm trend?"
                                        id="trendConfirmStreamCount"
                                        value={tank.tuningParams.trendConfirmStreamCount ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Unit Period for Stream?"
                                        id="streamUnitPeriod"
                                        value={tank.tuningParams.streamUnitPeriod ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Max life of a stream?"
                                        id="streamMaxTime"
                                        value={tank.tuningParams.streamMaxTime ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Drive Start timeout?"
                                        id="driveStartTimeout"
                                        value={tank.tuningParams.driveStartTimeout ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Halt Start Timeout?"
                                        id="haltStartTimeout"
                                        value={tank.tuningParams.haltStartTimeout ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Stop Speed cutoff?"
                                        id="stopSpeedCutoff"
                                        value={tank.tuningParams.stopSpeedCutoff ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Drive Speed cutoff?"
                                        id="driveSpeedCutoff"
                                        value={tank.tuningParams.driveSpeedCutoff ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Minimum Filling Duration?"
                                        id="minFillDuration"
                                        value={tank.tuningParams.minFillDuration ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Maximum Filling Duration?"
                                        id="maxFillDuration"
                                        value={tank.tuningParams.maxFillDuration ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Minimum Theft Duration?"
                                        id="minTheftDuration"
                                        value={tank.tuningParams.minTheftDuration ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Maximum Theft Duration?"
                                        id="maxTheftDuration"
                                        value={tank.tuningParams.maxTheftDuration ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Minimum Filling flowrate?"
                                        id="minFillFlowRatePerMinute"
                                        value={tank.tuningParams.minFillFlowRatePerMinute ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Maximum Filling Flowrate?"
                                        id="maxFillFlowRatePerMinute"
                                        value={tank.tuningParams.maxFillFlowRatePerMinute ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Minimum Theft Flowrate?"
                                        id="minTheftFlowRatePerMinute"
                                        value={tank.tuningParams.minTheftFlowRatePerMinute ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>
                                <FormControl sx={{ m: 1, width: '25ch' }} >
                                    <TextField
                                        label="Maximum Theft Flowrate?"
                                        id="maxTheftFlowRatePerMinute"
                                        value={tank.tuningParams.maxTheftFlowRatePerMinute ?? 0}
                                        onChange={(e) => updateTankTuning(e.target.id, e.target.value)}
                                        sx={{ m: 1, width: '22ch' }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">Liters: </InputAdornment>,
                                        }}
                                        variant="standard"
                                        type="number"
                                    />
                                </FormControl>

                            </Fragment>
                    }


                    <FormControl sx={{ m: 1, width: '30ch' }} >
                        <Button onClick={() => saveTank()} disabled={!formValid}>
                            Save Tank
                        </Button>
                    </FormControl>
                </div>
            </div>
        )
    }
}

export default TankForm